import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, Users, ArrowRight, Archive } from 'lucide-react';
import { studyGroups } from '../data/studyGroups';

const archivedGroups = studyGroups.filter(g => g.status === 'archived');
const allTags = [...new Set(archivedGroups.flatMap(g => g.tags))];

const StudyGroupsArchivePage = () => {
  const [selectedTags, setSelectedTags] = useState([]);
  const toggleTag = (tag) => setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  const filteredGroups = selectedTags.length === 0 ? archivedGroups : archivedGroups.filter(g => g.tags.some(tag => selectedTags.includes(tag)));

  return (
    <div className="min-h-screen pt-20" data-testid="study-groups-archive-page">
      <section className="py-12 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <Link to="/services/study-groups" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--accent-cyan)] mb-6 font-mono text-sm"><ArrowRight className="w-4 h-4 rotate-180" /> Back to Current Groups</Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 border border-[var(--text-muted)] bg-[var(--text-muted)]/10 flex items-center justify-center"><Archive className="w-8 h-8 text-[var(--text-muted)]" /></div>
              <div>
                <p className="font-mono text-sm uppercase tracking-wider text-[var(--text-muted)]">Past Groups</p>
                <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)]">Study Groups Archive</h1>
              </div>
            </div>
            <p className="text-[var(--text-muted)] text-lg md:text-xl leading-relaxed">Browse our completed study groups. Similar groups will be offered again in future cycles.</p>
          </div>
        </div>
      </section>

      {allTags.length > 0 && (
        <section className="py-8 border-y border-[var(--border)] ">
          <div className="container-main">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <span className="font-mono text-sm text-[var(--text-muted)] uppercase tracking-wider">Filter by Tag:</span>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (<button key={tag} onClick={() => toggleTag(tag)} className={`px-4 py-2 font-mono text-sm border transition-colors duration-200 ${selectedTags.includes(tag) ? 'border-[var(--text-muted)] bg-[var(--text-muted)]/20 text-[var(--text-main)]' : 'border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--text-muted)]/50'}`}>{tag}</button>))}
                {selectedTags.length > 0 && (<button onClick={() => setSelectedTags([])} className="px-4 py-2 font-mono text-sm border border-[var(--error)]/50 text-[var(--error)] hover:bg-[var(--error)]/10">Clear All</button>)}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-16">
        <div className="container-main space-y-8">
          {filteredGroups.length > 0 ? filteredGroups.map((group) => (
            <div key={group.id} className="bg-[var(--surface)] border border-[var(--border)] overflow-hidden opacity-75 hover:opacity-100 transition-opacity" data-testid={`archived-group-${group.id}`}>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-[var(--text-muted)]/20 text-[var(--text-muted)] text-xs font-mono uppercase">Completed</span>
                  <span className="px-3 py-1 bg-[var(--accent-cyan)]/10 text-[var(--accent-cyan)]/70 text-xs font-mono uppercase">{group.level}</span>
                </div>
                <h3 className="text-2xl font-bold text-[var(--text-main)] mb-2">{group.title}</h3>
                <p className="text-[var(--text-muted)] mb-6">{group.description}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-[var(--text-muted)]" /><div><p className="text-xs text-[var(--text-muted)]">Field</p><p className="text-sm text-[var(--text-main)] font-mono">{group.field}</p></div></div>
                  <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[var(--text-muted)]" /><div><p className="text-xs text-[var(--text-muted)]">Duration</p><p className="text-sm text-[var(--text-main)] font-mono">{group.duration}</p></div></div>
                  <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[var(--text-muted)]" /><div><p className="text-xs text-[var(--text-muted)]">Group Size</p><p className="text-sm text-[var(--text-main)] font-mono">{group.groupSize}</p></div></div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {group.tags.map((tag, idx) => (<span key={idx} className="px-2 py-1 bg-[var(--surface-highlight)] border border-[var(--border)] text-xs text-[var(--text-muted)] font-mono">{tag}</span>))}
                </div>
              </div>
            </div>
          )) : (
            <div className="text-center py-16">
              <Archive className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
              <p className="text-[var(--text-muted)] text-lg">No archived study groups found.</p>
              <Link to="/services/study-groups" className="mt-4 inline-block text-[var(--accent-cyan)] font-mono hover:underline">View Current Groups</Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default StudyGroupsArchivePage;
